import { apiGet, pickArray, pickObject } from './client'
import { config } from '@/config'
import type { CotProperty, CotTask, ObjectId, SmState, Track } from './types'
import { buildTrack, type CatalogEntry } from '@/domain/track'

const OBJECT_ID = /^[0-9a-fA-F]{24}$/

/* ============================================================
   Caché de propiedades
   Una sola llamada en lote para todos los nombres de una página
   (`ids[]=` trocea de a 100). Se cachea por id: los catálogos y las
   estaciones se repiten muchísimo entre incidencias.
   ============================================================ */
export class PropertyCache {
  private readonly store = new Map<ObjectId, CotProperty>()
  private readonly byCode = new Map<string, CotProperty>()

  get(id: ObjectId): CotProperty | undefined {
    return this.store.get(id)
  }

  getByCode(code: string): CotProperty | undefined {
    return this.byCode.get(code)
  }

  codeMap(): Map<string, CotProperty> {
    return this.byCode
  }

  /**
   * Resuelve propiedades por **código**, no por id.
   *
   * Algunos campos del schema guardan el código: `ot_provider` vale
   * `"p1110033"`. El endpoint dedicado `/api/v2/properties/code/{code}` exige
   * cabecera de administrador y con token de usuario responde 400, pero el
   * listado con `codes[]=` funciona y además viene en lote.
   */
  async ensureCodes(codes: string[], signal?: AbortSignal): Promise<void> {
    const missing = [...new Set(codes.filter((c) => c && !this.byCode.has(c)))]
    if (missing.length === 0) return

    for (let i = 0; i < missing.length; i += 100) {
      const chunk = missing.slice(i, i + 100)
      const payload = await apiGet<unknown>(
        '/api/v2/properties',
        { codes: chunk, limit: chunk.length },
        signal,
      )
      const props = pickArray<CotProperty>(payload, ['properties'])
      for (const p of props) {
        const c = codeOf(p)
        if (c) this.byCode.set(c, p)
      }
      this.put(props)
    }
  }

  put(properties: CotProperty[]): void {
    for (const p of properties) if (p?._id) this.store.set(p._id, p)
  }

  asMap(): Map<ObjectId, CotProperty> {
    return this.store
  }

  /**
   * Trae las que falten. Devuelve solo lo recién cargado.
   *
   * Se descarta lo que no sea un ObjectId. No es paranoia: algunos campos del
   * schema guardan un **código** y no un id —`ot_provider` vale `"p1110033"`—
   * y basta uno inválido en el lote para que la API responda 400 y se caiga la
   * consulta entera, no solo ese nombre. Verificado contra staging.
   */
  async ensure(ids: ObjectId[], signal?: AbortSignal): Promise<CotProperty[]> {
    const missing = [
      ...new Set(ids.filter((id) => id && OBJECT_ID.test(id) && !this.store.has(id))),
    ]
    if (missing.length === 0) return []

    const loaded: CotProperty[] = []
    for (let i = 0; i < missing.length; i += 100) {
      const chunk = missing.slice(i, i + 100)
      /*
       * `limit` es obligatorio aunque se pidan ids explícitos: verificado
       * contra staging el 11-ago, sin él la API devuelve **solo 10**
       * propiedades y descarta el resto sin ninguna señal de error. Eso
       * dejaba el catálogo de estados incompleto —las tareas caían en
       * "Estado no disponible"— y los campos del detalle en blanco.
       */
      const payload = await apiGet<unknown>(
        '/api/v2/properties',
        { ids: chunk, limit: chunk.length },
        signal,
      )
      const props = pickArray<CotProperty>(payload, ['properties'])
      if (props.length < chunk.length) {
        // Que no vuelva a pasar en silencio si la API cambia de criterio.
        console.warn(
          `[portal] se pidieron ${chunk.length} propiedades y llegaron ${props.length}. ` +
            'Los nombres o estados que falten se mostrarán vacíos.',
        )
      }
      loaded.push(...props)
    }
    this.put(loaded)
    return loaded
  }
}

/* ============================================================
   Catálogo de estados
   `GET /api/v1/tasks/{tg}/sm/smstate` devuelve todos los estados del flujo
   con su propiedad asociada. No requiere permisos de administrador y es
   contrato público, así que evita mantener una lista de ids a mano
   (corrección §2.1 y §4.4).
   ============================================================ */
export async function fetchTrack(
  taskGroupId: string,
  kind: 'inc' | 'ot',
  cache: PropertyCache,
  signal?: AbortSignal,
): Promise<Track> {
  const payload = await apiGet<unknown>(`/api/v1/tasks/${taskGroupId}/sm/smstate`, undefined, signal)
  const states = pickArray<SmState>(payload, ['smstates', 'states', 'values'])

  /*
   * Verificado contra staging el 11-ago: el catálogo trae la propiedad
   * embebida pero con `name` como STRING con el nombre visible —
   *   { _id, name: "4. Incidencia cerrada", translations: {...} }
   * — o sea, sin el código del estado. Y el código es con lo que el portal
   * identifica cada estado. Así que las propiedades se resuelven siempre
   * contra /api/v2/properties, que sí devuelve { code, display }.
   */
  const ids: ObjectId[] = []
  for (const s of states) {
    const id = typeof s.property === 'string' ? s.property : s.property?._id
    if (id) ids.push(id)
  }
  await cache.ensure(ids, signal)

  const entries: CatalogEntry[] = []
  for (const s of states) {
    const embedded = typeof s.property === 'object' && s.property ? s.property : undefined
    const id = typeof s.property === 'string' ? s.property : embedded?._id
    if (!id) continue
    const resolved = cache.get(id)
    const code = codeOf(resolved)
    // El display puede salir del catálogo embebido; el código, nunca.
    const display = displayOf(resolved) ?? displayOf(embedded)
    if (!code || !display) continue
    // `s._id` es el id del smState, distinto del de su propiedad. Es el que
    // entiende el filtro del listado.
    entries.push({ code, display, propertyId: id, smStateId: s._id })
  }

  return buildTrack(entries, kind)
}

function displayOf(p?: CotProperty): string | null {
  if (!p) return null
  const n = p.name
  if (typeof n === 'string') return n
  return n?.display ?? null
}
function codeOf(p?: CotProperty): string | null {
  if (!p) return null
  const n = p.name
  if (typeof n === 'string') return null
  return n?.code ?? null
}

/* ============================================================
   Listado de incidencias
   El portal carga el listado una sola vez, pero puede necesitar más de una
   página CRUDA para llenarlo (ver `collectPages`). Esas rondas se encadenan
   por fecha y no por número de página: con ~9.000 incidencias nuevas al mes,
   paginar por número produce duplicados y saltos cuando entran incidencias
   entre una ronda y la siguiente (corrección §2.2). Nunca `count=true`, que
   recorre 183.780 documentos y cuesta 560 ms.
   ============================================================ */

/**
 * La API topa en 100 por página (corrección §2.2). Como ya no se puede pedir
 * uno de más para saber si hay página siguiente, una página llena se toma
 * como "puede haber más": en el peor caso cuesta una ronda que vuelve vacía.
 */
const RAW_PAGE_LIMIT = 100

export interface RawPage {
  tasks: CotTask[]
  cursor: string | null
  hasMore: boolean
}

/**
 * Trae una página del listado.
 *
 * **Por qué el `GET` de v2 y no `POST /task/all`.** El 14-ago-2026 el listado
 * se cambió a `POST /api/v1/tasks/{tg}/task/all` porque el `GET` de v2 **ignora
 * en silencio todo filtro por estado**: se probaron seis formas —`smState_nin[]`,
 * `smState_nin`, `smState_ne`, `smState` como JSON `$nin`, `status_nin[]` y
 * `status_ne`— y las seis devolvieron el mismo conjunto que sin filtro, incluido
 * el control positivo `smState=<id>`. Los que sí honra son `createdAt_lt`,
 * `isActive`, `limit` y el orden.
 *
 * Ese cambio se midió **solo con un token de administrador**, y el supuesto de
 * que la autorización era la misma resultó falso: `POST /task/all` exige
 * permiso de administrador —`admin-tasks-read`— y responde **403** a cualquier
 * usuario normal. Medido el 27-ago-2026 con varias cuentas, entre ellas
 * `creador_de_incidencias`, que es la población objetivo del portal. No es v1
 * entero: `sm/smstate` y `task/{id}` —del que depende el BFF— sí funcionan con
 * el token del usuario. El que exige administrador es el listado masivo.
 *
 * Así que el listado vuelve a v2, que es donde se verificó la visibilidad real
 * el 11-ago con una cuenta de distribuidor: el admin recibía 100 incidencias de
 * 40 estaciones y ella 1 de la suya.
 *
 * El precio es perder el filtro por estado del servidor. El de cliente
 * —`LIST_EXCLUDED_INCIDENTS` en `repository.loadIncidents`— nunca dejó de ser
 * la garantía, y desde el 14-ago reconoce también los estados fuera del track,
 * que era el agujero por el que se colaba `incidents_rejected`. Como ahora las
 * páginas crudas vienen con estados excluidos mezclados, `loadIncidents` pide
 * más de una hasta llenar la suya.
 */
export async function fetchIncidentTasks(
  cursor: string | null,
  signal?: AbortSignal,
): Promise<RawPage> {
  const payload = await apiGet<unknown>(
    `/api/v2/task-groups/${config.TASK_GROUP_INCIDENTS}/tasks`,
    {
      limit: RAW_PAGE_LIMIT,
      sortBy: 'createdAt',
      orderBy: 'desc',
      createdAt_lt: cursor ?? undefined,
    },
    signal,
  )

  // Verificado el 11-ago: v2 responde { data: { values: [...] } }.
  // `tasks` queda como alternativa por si la forma cambia.
  const tasks = pickArray<CotTask>(payload, ['values', 'tasks'])
  const hasMore = tasks.length >= RAW_PAGE_LIMIT
  // El cursor sale de la lista CRUDA, antes de descartar estados excluidos:
  // si saliera de la filtrada, la ronda siguiente se saltaría elementos.
  const last = tasks[tasks.length - 1]
  return { tasks, cursor: last?.createdAt ?? null, hasMore }
}

/**
 * Relee una incidencia concreta. Es la misma llamada que el BFF usa como paso
 * de autorización, así que el usuario de estación la puede hacer con su token.
 * Devuelve la tarea pelada, sin envoltorio (verificado; ver shapes.test.ts).
 */
export async function fetchIncidentTask(
  incidentId: ObjectId,
  signal?: AbortSignal,
): Promise<CotTask | null> {
  const payload = await apiGet<unknown>(
    `/api/v1/tasks/${config.TASK_GROUP_INCIDENTS}/task/${incidentId}`,
    undefined,
    signal,
  )
  return pickObject<CotTask>(payload, ['task'])
}

export interface WorkOrderSource {
  property: CotProperty
  task: CotTask | null
  /** ¿Puede este usuario abrir la orden en Cotalker? Decide el enlace. */
  userCanOpen: boolean
}

/**
 * Pide al backend propio las órdenes de una incidencia.
 *
 * El cliente manda un id de **incidencia**, nunca uno de orden: el backend lo
 * valida con el token de este mismo usuario y solo entonces resuelve las
 * órdenes, usando una credencial de servicio para las tareas que el usuario no
 * puede ver. Así la credencial nunca se aplica sobre un identificador elegido
 * por el navegador. Ver bff/handler.mjs.
 */
export async function fetchWorkOrdersFor(
  incidentId: ObjectId,
  signal?: AbortSignal,
): Promise<WorkOrderSource[]> {
  const payload = await apiGet<{ workOrders?: WorkOrderSource[] }>(
    `/incidents/${incidentId}/work-orders`,
    undefined,
    signal,
    config.BFF_URL,
  )
  return Array.isArray(payload?.workOrders) ? payload.workOrders : []
}
